import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import Table from "../../../../components/Table";
import EpicEditModal from "./EpicModal";
import api from "../../../../services/api";
import { HiPencil } from "react-icons/hi";
import TopMenu from "./Composant/TopMenu";

export default ({ project, setProject }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [epics, setEpics] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    search: searchParams.get("search") || "",
    statuses: searchParams.get("statuses")?.split(",") || ["TODO", "IN_PROGRESS", "NEED_INFO"],
    priorities: searchParams.get("priorities")?.split(",") || [],
    modules: searchParams.get("modules")?.split(",") || [],
    resource_profile: searchParams.get("resource_profile")?.split(",") || [],
    is_sent_to_backlog: searchParams.get("is_sent_to_backlog") || "",
  });
  const [sortBy, setSortBy] = useState({ key: "priority", value: -1 });
  const [selectedEpics, setSelectedEpics] = useState([]);
  const [selectedEpic, setSelectedEpic] = useState(null);
  const [isEpicEditModalOpen, setIsEpicEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchEpics();
  }, [filters]);

  const fetchEpics = async () => {
    setLoading(true);
    try {
      const { data, ok } = await api.post("/epic/search", { project_id: project._id, ...filters });
      if (!ok) return toast.error("Failed to fetch roadmaps");
      // sort by created_at ( date value)
      // data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      // sort on module
      data.sort((a, b) => (a.module || "").localeCompare(b.module || ""));
      setEpics(data);

      const newSearchParams = new URLSearchParams(searchParams);
      if (filters.search) newSearchParams.set("search", filters.search);
      else newSearchParams.delete("search");
      if (filters.priorities.length > 0) newSearchParams.set("priorities", filters.priorities.join(","));
      else newSearchParams.delete("priorities");
      if (filters.statuses.length > 0) newSearchParams.set("statuses", filters.statuses.join(","));
      else newSearchParams.delete("statuses");
      if (filters.modules.length > 0) newSearchParams.set("modules", filters.modules.join(","));
      else newSearchParams.delete("modules");
      if (filters.resource_profile.length > 0) newSearchParams.set("resource_profile", filters.resource_profile.join(","));
      else newSearchParams.delete("resource_profile");
      if (filters.is_sent_to_backlog) newSearchParams.set("is_sent_to_backlog", filters.is_sent_to_backlog);
      else newSearchParams.delete("is_sent_to_backlog");
      setSearchParams(newSearchParams);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch roadmaps");
    } finally {
      setLoading(false);
    }
  };

  const totalNow = epics.filter((e) => e.priority === "NOW");
  const totalNext = epics.filter((e) => e.priority === "NEXT");
  const totalLater = epics.filter((e) => e.priority === "LATER");

  function selectEpic(e) {
    setSelectedEpic(e);
    setIsEpicEditModalOpen(true);
  }

  const handleSave = async (itemId, value) => {
    try {
      const { ok, data } = await api.put(`/epic/${itemId}`, value);
      if (!ok) return toast.error("Failed to update");

      const index = epics.findIndex((item) => item._id === itemId);
      const newEpics = [...epics];
      newEpics[index] = { ...newEpics[index], ...value };
      setEpics(newEpics);
    } catch (error) {
      console.error(error);
      toast.error("Failed to update");
    }
  };

  return (
    <div className="space-y-1">
      <TopMenu
        filters={filters}
        setFilters={setFilters}
        project={project}
        epics={epics}
        selectedEpics={selectedEpics}
        setSelectedEpic={setSelectedEpic}
        setSelectedEpics={setSelectedEpics}
        setIsEpicEditModalOpen={setIsEpicEditModalOpen}
        fetchEpics={fetchEpics}
      />
      {isEpicEditModalOpen && (
        <EpicEditModal
          epic={selectedEpic}
          project={project}
          setProject={setProject}
          onClose={(e) => {
            if (!selectedEpic) return;
            const index = epics.findIndex((item) => item._id === selectedEpic._id);
            const newEpics = [...epics];
            newEpics[index] = { ...newEpics[index], ...e };
            setEpics(newEpics);
            setSelectedEpic(null);
            setIsEpicEditModalOpen(false);
          }}
          onDelete={() => {
            const newEpics = epics.filter((item) => item._id !== selectedEpic._id);
            setEpics(newEpics);
            setSelectedEpic(null);
            setIsEpicEditModalOpen(false);
          }}
        />
      )}
      <Table
        total={epics.length}
        header={[
          {
            title: (
              <input
                type="checkbox"
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedEpics(epics);
                  } else {
                    setSelectedEpics([]);
                  }
                }}
              />
            ),
            position: "center",
          },
          { title: "Title", key: "title" },
          { title: "Priority", key: "priority" },
          { title: "Days Required", key: "days_required" },
          { title: "Estimation", key: "estimation" },
        ]}
        loading={loading}
        height="h-[32rem]"
        sort={{ [sortBy.key]: sortBy.value }}
        onSort={(e) => setSortBy(sortBy.key === e ? { key: e, value: sortBy.value * -1 } : { key: e, value: 1 })}>
        <AnimatePresence>
          {epics
            .sort((a, b) => {
              if (sortBy.key === "created_at") {
                return sortBy.value * (new Date(b.created_at) - new Date(a.created_at));
              }
              // Handle numeric fields
              if (["days_required", "estimation", "index"].includes(sortBy.key)) {
                return sortBy.value * ((a[sortBy.key] || 0) - (b[sortBy.key] || 0));
              }
              // Default string comparison
              return sortBy.value * (a[sortBy.key] || "").localeCompare(b[sortBy.key] || "");
            })
            .map((item, index) => {
              const selected = selectedEpics.some((e) => e._id === item._id);

              let color,
                priorityColor = "bg-white";
              let hoverColor = "hover:bg-blue-50";

              if (item.priority === "NEXT") {
                color = "bg-blue-200";
                priorityColor = "bg-blue-300";
                hoverColor = "hover:bg-blue-300";
              }
              if (item.priority === "LATER") {
                priorityColor = "bg-gray-300";
                color = "bg-gray-200";
                hoverColor = "hover:bg-gray-300";
              }

              return (
                <motion.tr
                  key={index}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className={`cursor-pointer ${color} group ${hoverColor} transition-colors duration-200`}>
                  <td
                    className="border px-4 py-2 cursor-pointer w-8"
                    onClick={(e) => {
                      const find = selectedEpics.some((e) => e._id === item._id);
                      if (find) {
                        setSelectedEpics(selectedEpics.filter((e) => e._id !== item._id));
                      } else {
                        setSelectedEpics([...selectedEpics, item]);
                      }
                    }}>
                    <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" checked={selected} onChange={() => {}} />
                  </td>
                  <td className="px-4 py-2 text-sm w-full border">
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <div className="relative flex items-center gap-2 flex-1 min-w-0">
                          <span className="font-medium shrink-0">#{item.index}</span>
                          <div>
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              onBlur={(e) => {
                                setEditingId(null);
                                handleSave(item._id, { title: e.target.textContent });
                              }}
                              onClick={() => setEditingId(item._id)}
                              className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                              {item.title}
                            </div>
                            {item.module && (
                              <div className="mt-1">
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{item.module}</span>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setSelectedEpic(item);
                              setIsEpicEditModalOpen(true);
                            }}
                            className={`absolute top-1/2 -translate-y-1/2 right-0 px-3 py-1.5 rounded-md flex items-center gap-2 bg-white border border-gray-200 shadow-sm
                                      ${editingId === item._id ? "hidden" : "invisible group-hover:visible"}`}>
                            <HiPencil className="w-4 h-4 text-gray-600" />
                            <span className="text-xs font-medium text-gray-600">Open</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td onClick={(e) => e.preventDefault()} className="border px-4 py-2 max-w-xs truncate">
                    <select
                      value={item.priority}
                      onChange={async (e) => {
                        const { ok, data } = await api.put(`/epic/${item._id}`, { priority: e.target.value });
                        if (!ok) return toast.error("Failed to update priority");
                        setEpics(epics.map((e) => (e._id === item._id ? data : e)));
                        toast.success("Priority updated!");
                      }}
                      className={`w-28 px-2 py-1 rounded text-xs ${priorityColor} cursor-pointer appearance-none hover:opacity-80`}>
                      <option value="NOW">NOW</option>
                      <option value="NEXT">NEXT</option>
                      <option value="LATER">LATER</option>
                    </select>
                  </td>

                  <td className="px-4 py-2 border">
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => handleSave(item._id, { days_required: Number(e.target.textContent), estimation: Number(e.target.textContent) * item.tjm })}
                      className="outline-none border-b border-transparent hover:border-gray-300 focus:border-blue-500 bg-transparent px-1 line-clamp-3 overflow-hidden cursor-text">
                      {item.days_required}
                    </div>
                  </td>
                  <td onClick={() => selectEpic(item)} className="border px-4 py-2 max-w-xs truncate">
                    {item.estimation ? `${formatNumberThreeDigits(item.estimation)} €` : ""}
                  </td>
                </motion.tr>
              );
            })}
        </AnimatePresence>
      </Table>

      {/* New separate table for totals */}
      <table className="w-full border-collapse border border-gray-200">
        <tbody>
          <TotalTable totalNow={totalNow} totalNext={totalNext} totalLater={totalLater} epics={epics} />
        </tbody>
      </table>
    </div>
  );
};

const TotalTable = ({ totalNow, totalNext, totalLater, epics }) => {
  return (
    <>
      <tr className="">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNow.length} epics</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">
          Short-term features that are priority and MUST for an MVP. These are the key features of a product in addition to the basics (admin + deployment + metabase). The features
          that will be developed in the MVP.{" "}
        </td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNow.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalNow.reduce((acc, item) => acc + (item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">NOW</td>
      </tr>
      <tr className="bg-blue-200">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNext.length} epics</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">The medium-term functionalities which enrich the product, without these the product already works. </td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalNext.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalNext.reduce((acc, item) => acc + (item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">NEXT</td>
      </tr>
      <tr className="bg-gray-200">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalLater.length} epics</td>
        <td className="border px-4 py-2 whitespace-normal text-sm">Long-term functionalities are functionalities which are not defined and difficult to estimate.</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{totalLater.reduce((acc, item) => acc + (item.days_required || 0), 0)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(totalLater.reduce((acc, item) => acc + (item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">LATER</td>
      </tr>
      <tr className="bg-sky-700 text-white font-bold">
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{epics.length} epics</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm"></td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{epics.reduce((acc, item) => acc + (item.days_required || 0), 0).toFixed(2)}</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">{formatNumberThreeDigits(epics.reduce((acc, item) => acc + (item.estimation || 0), 0))} €</td>
        <td className="border px-4 py-2 max-w-xs truncate text-sm">Total</td>
      </tr>
    </>
  );
};

const StatusTag = ({ item, onStatusChange }) => {
  if (!item?.status) return null;
  const colors = {
    TODO: "bg-blue-600 text-xs text-white border-gray-700",
    IN_PROGRESS: "bg-blue-100 text-xs text-blue-800",
    NEED_INFO: "bg-yellow-100 text-xs text-yellow-800",
    DONE: "bg-green-100 text-xs text-green-800",
  };

  const handleStatusChange = (e) => {
    e.stopPropagation();
    onStatusChange(e.target.value);
  };

  return (
    <select className={`w-28 px-2 py-1 rounded ${colors[item?.status]} cursor-pointer appearance-none hover:opacity-80`} value={item?.status} onChange={handleStatusChange}>
      <option value="TODO">TODO</option>
      <option value="IN_PROGRESS">IN PROGRESS</option>
      <option value="NEED_INFO">NEED INFO</option>
      <option value="DONE">DONE</option>
    </select>
  );
};

const formatNumberThreeDigits = (number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};
